import { memo, useEffect } from 'react';

import { KeyValue } from '@/components/ui/keyValue/keyValue';
import { Widget } from '@/components/ui/widget';
import { isWidgetVisible } from '@/components/ui/widget/utils/widget.utils';
import type { FileView } from '@/components/ui/widget/views/file/file.view.type';
import type { CoreViewType } from '@/components/ui/widget/views/views.type';
import { getByPath } from '@/utils/widgets';
import type { WidgetData } from '../../widget.type';
import type { KeyValueView } from './keyValue.type';

const localStorageKey = 'keyValueArraysCollapsed' as const;

const storeCollapsedState = (
  traceId: string,
  arrayKey: string,
  collapsedState: boolean
) => {
  let storage: Record<string, Record<string, boolean>>;
  try {
    const item = localStorage.getItem(localStorageKey);
    if (!item) {
      throw new Error('No item found in localStorage');
    }
    // Check if storage is a valid parsable JSON string
    storage = JSON.parse(item);
    // If no error, check if it is not null and that it is a real object (and not an array since the type of an array is 'object' in javascript)
    storage =
      !!storage && typeof storage === 'object' && !Array.isArray(storage)
        ? storage // If yes keep the value
        : {}; // If not, set the value as an empty object
  } catch {
    // If there is an error (this would happen at the JSON.parse), set the value as an empty object
    storage = {};
  }
  // Create the trace's key as an empty object if it doesn't exist yet
  storage[traceId] = storage[traceId] || {};
  // Store the collapsed state of the array's key
  storage[traceId][arrayKey] = collapsedState;
  // Store the complete object as a string in the localStorage key
  localStorage.setItem(localStorageKey, JSON.stringify(storage));
};

const getCollapsedState = (traceId: string, arrayKey: string) => {
  try {
    const item = localStorage.getItem(localStorageKey);
    if (!item) return;
    const storage = JSON.parse(item);
    return storage?.[traceId]?.[arrayKey];
  } catch {
    return;
  }
};

type Props = {
  view: KeyValueView;
  data: WidgetData;
  setSelectableItemData?: (data: unknown) => void;
};

const getPath = (view: CoreViewType | FileView) => {
  switch (view.type) {
    case 'file':
      return view.fileDataPath;
    default:
      return view.path;
  }
};

const KeyValueViewImpl = memo(
  ({ view, data, setSelectableItemData }: Props) => {
    const { key, value } = view;

    const path = getPath(value.view);
    let dataValueByPath: unknown;
    if (path) {
      dataValueByPath = getByPath<unknown>(data, path);
    }

    useEffect(() => {
      if (setSelectableItemData) {
        setSelectableItemData(dataValueByPath);
      }
    }, [dataValueByPath]);

    const {
      meta: { traceId }
    } = data;

    // Check if value is an array and if it should be collapsed (when the array contains more than 1 item)
    const isCollapsable =
      // @ts-expect-error need to ignore this since arrayView does not have a path property
      // including it in the CoreViewType would break lot of types
      value.view.type === 'array' &&
      Array.isArray(dataValueByPath) &&
      dataValueByPath?.length > 1;

    const localStorageState = getCollapsedState(traceId, key);

    const isCollapsed = localStorageState ?? isCollapsable;

    // Event handler
    const toggleCollapse = () => {
      storeCollapsedState(traceId, key, !isCollapsed);
    };

    const isVisible = isWidgetVisible(dataValueByPath);
    if (!isVisible) {
      return null;
    }

    return (
      <KeyValue
        dataCyName={value?.view?.path}
        isCollapsable={isCollapsable}
        isCollapsed={isCollapsed}
        onToggleCollapse={toggleCollapse}
        title={key}
      >
        {/* @ts-expect-error should be fixed when Widget is typed */}
        <Widget data={data} widget={value} />
      </KeyValue>
    );
  }
);

export const keyValueViewProvider = {
  component: KeyValueViewImpl
};
